import React, { useEffect, useState } from 'react';
import { fillFor } from '../wall/wallTokens';
import { T, RADIUS, BODY_FONT } from './messagesModel';
import { LockIcon } from './SecureLockSheet';
import { WINDOW_CHOICES, LOCK_TYPE_LABEL, mmss } from './secureModel';
import messageService from '../../services/messageService';

/** Lucide `calendar-plus` at 13px (a substitute, flagged in the plan-detection handoff). */
export function CalendarPlusIcon({ size = 13 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="square" aria-hidden>
      <rect x="3" y="5" width="18" height="16" /><path d="M3 10h18M8 3v4M16 3v4M12 13v5M9.5 15.5h5" />
    </svg>
  );
}

/**
 * The PLAN DETECTION block's state: the account switch (off by default — the feature reads chats)
 * and one row per chat. Loaded when the sheet opens; each flip writes through and reverts on a
 * failure. `initial` lets a test (or a parent that already has it) skip the fetch.
 */
export function usePlanSettings(initial = null) {
  const [settings, setSettings] = useState(initial);
  useEffect(() => {
    if (initial) return undefined;
    let cancelled = false;
    Promise.resolve(messageService.getPlanDetection?.()).then((r) => { if (!cancelled && r?.success && r.data) setSettings(r.data); });
    return () => { cancelled = true; };
  }, [initial]);
  const setGlobal = async (enabled) => {
    const prev = settings;
    setSettings((s) => ({ ...(s || { threads: [] }), enabled }));
    const r = await messageService.setPlanDetection(enabled);
    if (!r?.success) setSettings(prev); else if (r.data) setSettings(r.data);
  };
  const setThread = async (threadId, enabled) => {
    const prev = settings;
    setSettings((s) => ({ ...s, threads: (s?.threads || []).map((t) => (t.threadId === threadId ? { ...t, enabled } : t)) }));
    const r = await messageService.setThreadPlanDetection(threadId, enabled);
    if (!r?.success) setSettings(prev);
  };
  return { settings, setGlobal, setThread };
}

/**
 * The gear beside + (Oct 2026): one white card over Messages. Today it holds the
 * secure-chat settings — how long an unlock lasts, the lock itself, LOCK NOW.
 *
 *   secure   useSecureChats
 *   onClose
 */
export default function MessagesSettingsSheet({ secure, onClose, planSettings = null }) {
  const { session } = secure;
  const plan = usePlanSettings(planSettings);
  const planOn = !!plan.settings?.enabled;
  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape') onClose(); }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const label = { fontSize: 9, fontWeight: 700, letterSpacing: '.14em', color: T.grey500 };
  const pill = (on, extra = {}) => ({
    height: 32, padding: '0 14px', borderRadius: RADIUS.pill, border: '2px solid transparent', fontSize: 10, fontWeight: 700, letterSpacing: '.08em',
    background: fillFor(on ? 'on' : 'off'), color: on ? T.white : T.ink, cursor: 'pointer', transition: 'color 200ms', ...extra,
  });

  return (
    <div data-messages-settings-sheet role="dialog" aria-modal="true" aria-label="Messages settings" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }} style={{ position: 'absolute', inset: 0, zIndex: 94, background: 'rgba(0,0,0,.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 'clamp(12px, 3vw, 24px)' }}>
      {/* The card never outgrows the viewport: the SETTINGS line stays put and everything under it scrolls. */}
      <div className="font-mono" data-testid="messages-settings-card" style={{ width: 400, maxWidth: '100%', maxHeight: '100%', background: T.white, color: T.ink, borderRadius: RADIUS.modal, padding: '22px 0 0', display: 'flex', flexDirection: 'column', boxSizing: 'border-box', overflow: 'hidden' }}>
        <div className="flex items-center justify-between" style={{ padding: '0 24px 18px', flex: 'none' }}>
          <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: '.12em' }}>SETTINGS</span>
          <button type="button" onClick={onClose} aria-label="Close" style={{ background: 'none', border: 0, padding: 4, display: 'flex', cursor: 'pointer' }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="square"><path d="M6 6l12 12M18 6L6 18" /></svg>
          </button>
        </div>

        <div data-testid="messages-settings-body" style={{ flex: '1 1 auto', minHeight: 0, overflowY: 'auto', WebkitOverflowScrolling: 'touch', padding: '0 24px 20px', display: 'flex', flexDirection: 'column', gap: 18 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: 16, borderRadius: RADIUS.row, background: T.canvas }}>
          <span className="flex items-center" style={{ gap: 8, fontSize: 11, fontWeight: 700, letterSpacing: '.1em' }}>
            <LockIcon size={13} />
            SECURE CHATS
          </span>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <span style={label}>LOCKS AFTER</span>
            <div className="flex" style={{ gap: 8 }}>
              {WINDOW_CHOICES.map((m) => (
                <button
                  key={m}
                  type="button"
                  aria-pressed={session.windowMinutes === m}
                  disabled={!session.hasLock}
                  onClick={() => secure.setWindow(m)}
                  style={pill(session.windowMinutes === m, { opacity: session.hasLock ? 1 : 0.4, cursor: session.hasLock ? 'pointer' : 'default' })}
                >
                  {m} MIN
                </button>
              ))}
            </div>
            <span style={{ fontFamily: BODY_FONT, fontSize: 12, lineHeight: 1.4, color: T.grey500 }}>
              {session.hasLock
                ? `After you unlock, secure chats stay open this long${secure.unlocked ? ` — ${mmss(secure.msLeft)} left now` : ''}.`
                : 'Set a lock first. Moving any thread to secure asks for one.'}
            </span>
          </div>

          {secure.bio?.supported && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <span style={label}>UNLOCK WITH {secure.bio.label}</span>
              <div className="flex items-center" style={{ gap: 8 }}>
                <button type="button" data-biometric-toggle aria-pressed={secure.bio.enrolled} disabled={!session.hasLock} onClick={() => (secure.bio.enrolled ? secure.disableBiometric() : secure.enableBiometric())} style={pill(secure.bio.enrolled, { opacity: session.hasLock ? 1 : 0.4 })}>
                  {secure.bio.enrolled ? 'ON' : 'OFF'}
                </button>
                <span style={{ fontFamily: BODY_FONT, fontSize: 12, lineHeight: 1.4, color: T.grey500 }}>
                  {secure.bio.enrolled ? 'This device opens secure chats with your face or finger.' : 'Skip the code on this device. Your PIN still works everywhere.'}
                </span>
              </div>
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <span style={label}>LOCK</span>
            <div className="flex items-center flex-wrap" style={{ gap: 8 }}>
              <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.08em' }}>{session.hasLock ? LOCK_TYPE_LABEL[session.type] : 'NONE'}</span>
              <button type="button" onClick={() => { onClose(); secure.changeLock(); }} style={pill(false)}>
                {session.hasLock ? 'CHANGE LOCK' : 'SET A LOCK'}
              </button>
              {secure.unlocked && (
                <button type="button" onClick={() => { onClose(); secure.lockNow(); }} style={pill(true)}>LOCK NOW</button>
              )}
            </div>
          </div>
        </div>

        {/* Plan detection (Oct 2026, design_handoff_plan_detection §6): the same pattern as the block above. */}
        <div data-plan-settings style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: 16, borderRadius: RADIUS.row, background: T.canvas }}>
          <span className="flex items-center" style={{ gap: 8, fontSize: 11, fontWeight: 700, letterSpacing: '.1em' }}>
            <CalendarPlusIcon size={13} />
            PLAN DETECTION
          </span>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <span style={label}>SUGGEST PLANS</span>
            <div className="flex items-center" style={{ gap: 8 }}>
              <button type="button" data-plan-global aria-label="Suggest plans" aria-pressed={planOn} disabled={!plan.settings} onClick={() => plan.setGlobal(!planOn)} style={pill(planOn, { opacity: plan.settings ? 1 : 0.4 })}>
                {planOn ? 'ON' : 'OFF'}
              </button>
              <span style={{ fontFamily: BODY_FONT, fontSize: 12, lineHeight: 1.4, color: T.grey500 }}>
                {planOn ? 'Spots plans in your chats and offers a Portal or Event. Only you see it.' : 'Off. FIND A PLAN still works in any chat.'}
              </span>
            </div>
          </div>

          <div data-plan-per-chat style={{ display: 'flex', flexDirection: 'column', gap: 6, opacity: planOn ? 1 : 0.4, transition: 'opacity 200ms' }}>
            <span style={label}>PER CHAT</span>
            {(plan.settings?.threads || []).length === 0 && (
              <span style={{ fontSize: 10, letterSpacing: '.08em', color: T.grey400 }}>NO CHATS YET.</span>
            )}
            {(plan.settings?.threads || []).map((t) => (
              <div key={t.threadId} className="flex items-center" style={{ gap: 10 }} data-plan-thread={t.threadId}>
                <span style={{ flex: 1, minWidth: 0, fontSize: 11, fontWeight: 700, letterSpacing: '.06em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{(t.name || 'CHAT').toUpperCase()}</span>
                <button type="button" aria-label={`Plan detection in ${t.name || 'this chat'}`} aria-pressed={t.enabled} disabled={!planOn} onClick={() => plan.setThread(t.threadId, !t.enabled)} style={pill(t.enabled, { height: 28, minWidth: 52, padding: '0 12px', fontSize: 9, cursor: planOn ? 'pointer' : 'default' })}>
                  {t.enabled ? 'ON' : 'OFF'}
                </button>
              </div>
            ))}
            <span style={{ fontFamily: BODY_FONT, fontSize: 12, lineHeight: 1.4, color: T.grey500 }}>Also in each chat's ⋯ menu. FIND A PLAN works even when a chat is off.</span>
          </div>

          <span className="flex items-center" style={{ gap: 6, fontSize: 9, fontWeight: 700, letterSpacing: '.1em', color: T.grey500 }}>
            <LockIcon size={13} />
            SECURE CHATS ARE NEVER READ
          </span>
        </div>

        <span style={{ fontSize: 9, letterSpacing: '.1em', color: T.grey400, textAlign: 'center', lineHeight: 1.6 }}>
          SECURE CHATS ARE YOURS ALONE · THE OTHER SIDE NEVER SEES THE LOCK
          <br />
          SUGGESTIONS ARE YOURS ALONE · NOTHING IS CREATED UNTIL YOU PUBLISH
        </span>
        </div>
      </div>
    </div>
  );
}
